import React from "react";
import { SectionWithShapes, SectionTitle } from "../common";
import { useLearningPackages } from "../../hooks/useLearningPackages";
import { API_BASE_URL } from "../../constants/api";
import type { LearningPackagePayload } from "../../services/learningPackageService";

function formatPublishedDate(value: string | null | undefined): string {
  if (value == null || value === "") return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function buildFileUrl(file: string): string {
  if (!file) return "#";
  if (file.startsWith("http://") || file.startsWith("https://")) return file;
  const base = API_BASE_URL.replace(/\/api\/?$/, "");
  return file.startsWith("/") ? `${base}${file}` : `${base}/${file}`;
}

function buildCoverUrl(file_cover: string): string {
  if (!file_cover) return "";
  if (file_cover.startsWith("http://") || file_cover.startsWith("https://")) return file_cover;
  const base = API_BASE_URL.replace(/\/api\/?$/, "");
  return file_cover.startsWith("/") ? `${base}${file_cover}` : `${base}/${file_cover}`;
}

const LearningPackageCard: React.FC<{ item: LearningPackagePayload; index: number }> = ({
  item,
}) => {
  const coverUrl = buildCoverUrl(item.file_cover) || "/images/initiatives/Picture1.png";
  const fileUrl = buildFileUrl(item.file);
  const publishedLabel = formatPublishedDate(item.published_date);

  return (
    <div className="col-lg-6 mb-4">
      <div className="initiative-card learning-package-card">
        <div className="initiative-card-image">
          <img src={coverUrl} alt={item.eng_title} />
        </div>
        <div className="learning-package-card__body">
          <h4 className="text-center mb-3">{item.eng_title}</h4>
          {item.ch_title && (
            <p className="text-center mb-2" style={{ fontSize: "0.95rem", color: "#273351" }}>
              {item.ch_title}
            </p>
          )}
          {publishedLabel && (
            <p className="text-center mb-4" style={{ fontSize: "0.875rem", color: "#6c757d" }}>
              Published: {publishedLabel}
            </p>
          )}
          <div className="text-center">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-main btn-line mx-2"
              download
            >
              <i className="fas fa-file-pdf me-2" aria-hidden />
              Download PDF
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Learning Package section: fetches from /learning-packages and displays
 * cards with file_cover, eng_title, ch_title, published_date, and Download PDF.
 */
const LearningPackage: React.FC = () => {
  const { packages, loading, error } = useLearningPackages();

  return (
    <SectionWithShapes id="learning-package">
      <SectionTitle
        subtitle="[ OUR INITIATIVES ]"
        title="Learning Package"
        textCenter
      />
      <p className="learning-package__intro text-center">
        Download our comprehensive learning materials and resources for AI-integrated
        science education.
      </p>
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading learning packages...</span>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-4">
          <p className="text-muted mb-0">Unable to load learning packages. Please try again later.</p>
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted mb-0">No learning packages available yet.</p>
        </div>
      ) : (
        <div className="row justify-content-center">
          {packages.map((item, index) => (
            <LearningPackageCard key={item.id ?? index} item={item} index={index} />
          ))}
        </div>
      )}
    </SectionWithShapes>
  );
};

export default LearningPackage;
